/**
 * Fecha o menu de um `react-select` portalizado quando a página rola.
 *
 * Por que existe: os selects da aplicação usam
 * `menuPortalTarget={document.body}` com `zIndex: 2000` para o menu não ser
 * cortado por `overflow` de card, tabela ou drawer. O menu portalizado é
 * posicionado nas coordenadas que o campo tinha no momento da abertura, então
 * ao rolar a página ele deixa de acompanhar o campo — e, com z-index 2000,
 * passa a ser pintado POR CIMA do header, flutuando solto na tela.
 *
 * Por que a forma de FUNÇÃO, e não `closeMenuOnScroll={true}`: o booleano do
 * react-select só fecha quando o alvo do scroll é o próprio documento
 * (`isDocumentElement`). Nesta aplicação o conteúdo rola dentro de uma `div`
 * com `overflow-y-auto` (`components/layout/index.tsx`), não no documento —
 * então o booleano nunca dispararia.
 *
 * Por que `[role="listbox"]`: o react-select escuta o scroll em CAPTURA no
 * document, logo ele também vê a própria lista do menu rolando — e fechar aí
 * tornaria uma lista longa inutilizável. O `MenuList` é o único elemento com
 * `role="listbox"` na árvore do menu. Não usar classe do emotion nesse teste:
 * os rótulos (`...-MenuList`) existem em desenvolvimento e são removidos no
 * build de produção, então o teste passaria aqui e falharia em produção.
 */
export const fecharMenuAoRolar = (event: Event): boolean => {
  const alvo = event.target;
  if (!(alvo instanceof HTMLElement)) return true;
  return !alvo.closest('[role="listbox"]');
};

/**
 * Conjunto de props que TODO select portalizado da aplicação deve espalhar.
 * Use `{...propsMenuSelect}` — não aplique `closeMenuOnScroll` sozinho.
 *
 * Por que virou pacote: reportado em 13/08/2026 que o select de contrapartida
 * "não abria" para parte do time. Não era permissão nem dado — era altura de
 * tela. Quando não há espaço abaixo do campo, o react-select rola o container
 * para trazer o menu à vista (`menuShouldScrollIntoView`, ligado por padrão).
 * Esse scroll é programático, mas indistinguível de um scroll do usuário para
 * o `closeMenuOnScroll` acima — que então fecha o menu no mesmo quadro em que
 * ele abriu. Com espaço sobrando não há rolagem, não há fechamento, e o menu
 * abre normalmente: por isso só aparecia em monitor menor, e por isso parecia
 * intermitente ("às vezes abre").
 *
 * - `menuShouldScrollIntoView: false` tira o scroll automático, que é o que
 *   dispara o fechamento espúrio.
 * - `menuPlacement: 'auto'` é o par obrigatório: sem o scroll, o menu precisa
 *   saber abrir PARA CIMA quando não cabe embaixo, senão nasceria fora da área
 *   visível — trocaria um sintoma por outro.
 */
export const propsMenuSelect = {
  closeMenuOnScroll: fecharMenuAoRolar,
  menuPlacement: 'auto' as const,
  menuShouldScrollIntoView: false,
};
